import { ChangeEvent, useCallback, useMemo } from "react";
import { InputProps } from "./Input.types";
import "./Input.scss";

export const Input: React.FC<InputProps> = ({
  label,
  value,
  onChange,
  type,
  disabled = false,
  required,
}) => {
  /**
   * Holds the actual value of the input
   */
  const valueToDisplay: string | number | undefined = useMemo(():
    | string
    | number
    | undefined => {
    if (type === "time") {
      if (!value) return "--:--";
      const hours: string = (value / 60).toFixed(0);
      const minutes: string = (value % 60).toFixed(0);
      return `${hours}:${minutes}`;
    } else if (type === "date") {
      if (!(value instanceof Date)) return undefined;
      return value?.toISOString().split("T")[0];
    } else return value;
  }, [type, value]);

  /**
   * Updates the value of the input depending of the type
   */
  const handleOnChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      if (type === "number")
        if (evt.target.value) onChange(Number(evt.target.value));
        else onChange(undefined!);
      else if (type === "date")
        onChange(evt.target.value ? new Date(evt.target.value) : undefined);
      else if (type === "time") {
        const timeString: string = evt.target.value;
        if (!timeString) return onChange(0);
        const [hours, minutes] = timeString.split(":").map(Number);
        const totalMinutes: number = hours * 60 + minutes;
        onChange(totalMinutes);
      } else onChange(evt.target.value);
    },
    [onChange, type]
  );

  /**
   * The actual html input component
   */
  const actualInput: React.JSX.Element = useMemo((): React.JSX.Element => {
    return (
      <input
        required={required}
        disabled={disabled}
        data-testid="input-component-input"
        id={`input-component-${label}`}
        className="input-component__input"
        value={valueToDisplay}
        type={type}
        step="any"
        onChange={handleOnChange}
      />
    );
  }, [disabled, handleOnChange, label, required, type, valueToDisplay]);

  return (
    <div className="input-component__wrapper">
      <label
        htmlFor={`input-component-${label}`}
        className="input-component__label"
      >
        {label}
      </label>
      {actualInput}
    </div>
  );
};
